// src/lib/lazyWithRetry.js
//
// Wrapper around React.lazy() that handles the SPA stale-cache failure
// mode after every deploy. Without this, Safari users (and to a lesser
// extent Chrome/Firefox users) periodically see ErrorBoundary's
// "Something went wrong" page after we ship.
//
// ─── The bug ────────────────────────────────────────────────────────
// Every code-split chunk in build/static/js/ is content-hashed, e.g.
// `115.91d2ba1d.chunk.js`. The main bundle (and prerendered HTML)
// references chunks by these exact hashes. When we deploy:
//
//   1. Cloudflare Pages atomically swaps in a new build with NEW hashes.
//   2. Safari (more aggressively than other browsers, especially with
//      `Cache-Control: must-revalidate`) keeps serving the OLD index.html
//      from its HTTP cache.
//   3. The old index.html references chunk hash `115.91d2ba1d.chunk.js`,
//      which no longer exists on origin. Server returns 404.
//   4. React.lazy()'s import() promise rejects with a chunk-load error.
//   5. The error bubbles to ErrorBoundary → user sees "Something went
//      wrong" — and because @sentry/browser is also lazy-loaded, the
//      error often doesn't even reach Sentry before the user gives up.
//
// User-visible signal: site works in Chrome, breaks in Safari, "fixes
// itself" after the user opens DevTools (which forces a hard refresh
// that bypasses HTTP cache).
//
// ─── The fix ────────────────────────────────────────────────────────
// On chunk-load failure, do a one-shot `window.location.reload()`. The
// reload sends `Cache-Control: max-age=0` for the navigation request,
// so Safari (or whatever browser) re-fetches index.html from origin
// and gets the current hashes. Subsequent chunk imports succeed.
//
// We use sessionStorage with a timestamp to prevent infinite reload
// loops if the reload itself produces the same error (which would be
// a real bug, not a stale cache). Only one auto-reload per 30 s window;
// after that the error is allowed to surface to ErrorBoundary so the
// user at least gets the "refresh page" button rather than a reload
// loop they can't escape.
//
// ─── Why not just disable HTTP caching for index.html ────────────────
// We DO already set `Cache-Control: max-age=0, must-revalidate` for
// `/` and `/index.html` in public/_headers. Safari ignores this under
// network pressure (slow 4G, backgrounded tab, low memory). The retry
// here is the belt-and-braces complement.

import { lazy } from 'react';

const STORAGE_KEY  = 'aen-chunk-reload-at';
const RETRY_WINDOW = 30_000; // 30 s — long enough for one reload, short enough to allow real errors through.

// Registry of every memoised factory created by lazyWithRetry. Used by
// warmupAll() (below) to pre-resolve every route chunk before
// ReactDOM.hydrateRoot runs against the static-skeleton in public/index.html.
// Without this pre-warm, every React.lazy() boundary throws a Promise on
// first render, Suspense substitutes the fallback, and the prerendered/
// static DOM is thrown away → React error #418 → re-render from scratch
// (the regression mode that killed Path A — see scripts/prerender.mjs's
// "PRERENDER is opt-in" comment for the historical context).
//
// Pushing a memoised factory means warmupAll() and React.lazy()'s internal
// renderer await the SAME Promise per chunk — webpack would dedupe the
// network fetch anyway, but React.lazy() tracks resolution timeline
// per-factory, so without the shared Promise the prewarm wouldn't actually
// help React's render path. Module-scoped array → zero-cost on routes
// that don't hydrate (CSR builds simply never call warmupAll()).
const memoisedFactories = [];

/**
 * Detect chunk-load errors across browsers. Each engine reports them
 * with slightly different messages — keep this list permissive.
 *
 *   • Webpack 5 (CRA): "Loading chunk N failed", "ChunkLoadError"
 *   • Native ESM (Vite/Rollup): "Failed to fetch dynamically imported module"
 *   • Safari fetch: "Importing a module script failed"
 *   • Generic network: "Failed to fetch"
 */
function isChunkLoadError(err) {
  if (!err) return false;
  const msg  = err.message || '';
  const name = err.name || '';
  if (name === 'ChunkLoadError') return true;
  return /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|Failed to fetch/i.test(msg);
}

/**
 * Drop-in replacement for React.lazy() that handles chunk-load failures.
 *
 * Usage — identical to React.lazy:
 *
 *   import lazyWithRetry from '../lib/lazyWithRetry';
 *
 *   const Home = lazyWithRetry(() =>
 *     import(/* webpackChunkName: "home" *\/ './pages/Home')
 *   );
 *
 * On the first chunk-load failure within a tab, the helper does a
 * one-shot `window.location.reload()`. The reload sends a fresh navigation
 * request that bypasses HTTP cache → Safari (or whatever browser) gets
 * the current index.html → the new chunk hashes match what's on origin.
 *
 * Subsequent failures within RETRY_WINDOW are allowed to bubble to
 * ErrorBoundary — that prevents an infinite reload loop if the underlying
 * issue is a real bug rather than a stale cache.
 */
export default function lazyWithRetry(factory) {
  // Memoise the import promise so React.lazy() AND warmupAll() await the
  // SAME Promise per chunk. webpack dedupes the network fetch anyway, but
  // React.lazy() tracks resolution timeline per-factory — without the
  // shared Promise, calling warmupAll() spawns a separate in-flight import
  // and React.lazy() sees a fresh unresolved factory on render, defeating
  // the prewarm.
  let memoisedPromise = null;

  function memoFactory() {
    if (memoisedPromise) return memoisedPromise;
    memoisedPromise = (async () => {
      try {
        return await factory();
      } catch (err) {
        if (!isChunkLoadError(err)) throw err;

        // sessionStorage is per-tab, which is what we want — a reload from
        // one tab shouldn't suppress reloads in a different tab.
        let lastReloadAt = 0;
        try {
          lastReloadAt = parseInt(window.sessionStorage.getItem(STORAGE_KEY) || '0', 10) || 0;
        } catch { /* sessionStorage disabled — fall through to throw */ }

        const now = Date.now();
        if (now - lastReloadAt > RETRY_WINDOW) {
          try { window.sessionStorage.setItem(STORAGE_KEY, String(now)); } catch { /* see above */ }
          // eslint-disable-next-line no-console
          console.warn('[lazyWithRetry] chunk load failed, forcing reload:', err && err.message);
          if (typeof window !== 'undefined' && window.location) window.location.reload();
          // Never-resolving Promise so React doesn't try to render an error
          // fallback in the brief window before the reload happens.
          return new Promise(() => {});
        }

        // Already retried within the window — let the error bubble so
        // ErrorBoundary shows the user-visible refresh page.
        // eslint-disable-next-line no-console
        console.error('[lazyWithRetry] chunk load failed AGAIN within retry window — surfacing to ErrorBoundary');
        throw err;
      }
    })();
    return memoisedPromise;
  }

  memoisedFactories.push(memoFactory);
  return lazy(memoFactory);
}

/**
 * Pre-resolve every chunk registered by lazyWithRetry(). Call this BEFORE
 * ReactDOM.hydrateRoot when hydrating the static-skeleton, so React's
 * Suspense boundaries don't fire on first render and the DOM survives
 * intact.
 *
 * Uses Promise.allSettled — a single chunk failure mustn't block hydration
 * of the rest of the app; the standard chunk-load-retry path above already
 * handles 404s by reloading the tab.
 */
export function warmupAll() {
  return Promise.allSettled(memoisedFactories.map((f) => f()));
}
